import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Plus, Save, Trash2, Eye, EyeOff,
  HelpCircle, Lock, GripVertical,
} from "lucide-react";

/**
 * FAQManagementPage — founder-only CMS for the pre-launch/launch FAQs.
 *
 * Features:
 *  • Add — appended to the end of the current page's list (server assigns next `order`)
 *  • Inline edit — question + answer are textareas; per-row Save Changes button
 *  • Delete — confirms then hard-deletes
 *  • Toggle active — hide a question without deleting it
 *  • Reorder — drag the handle (⋮⋮) to drop a question into a new position; server rewrites all `order` fields
 *  • Page tabs — switch between prelaunch / launch (launch starts empty until founder adds questions)
 *
 * Admins (non-founder) get read-only view with a "founder access required" banner —
 * the server enforces the same with 403 on any mutation.
 */
const FAQManagementPage = () => {
  const { user } = useAuth();
  const [page, setPage] = useState("prelaunch");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newDraft, setNewDraft] = useState({ question: "", answer: "" });
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const isFounder = useMemo(() => {
    const email = (user?.email || "").toLowerCase();
    return (
      email === "ramneetrdh@gmail.com" ||
      email === "admin@dhdh.ca" ||
      user?.admin_role === "founder"
    );
  }, [user]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/faqs?page=${encodeURIComponent(page)}`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load FAQs");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [page]);

  const handleFieldChange = (faq_id, field, value) => {
    setItems(prev => prev.map(it => it.faq_id === faq_id ? { ...it, [field]: value, _dirty: true } : it));
  };

  const handleSaveRow = async (faq_id) => {
    const row = items.find(it => it.faq_id === faq_id);
    if (!row) return;
    setSavingId(faq_id);
    try {
      const res = await axios.put(`${API}/admin/faqs/${faq_id}`, {
        question: row.question,
        answer: row.answer,
        is_active: row.is_active,
      });
      setItems(prev => prev.map(it => it.faq_id === faq_id ? { ...res.data, _dirty: false } : it));
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const handleToggleActive = async (faq_id) => {
    const row = items.find(it => it.faq_id === faq_id);
    if (!row) return;
    const nextActive = !row.is_active;
    setItems(prev => prev.map(it => it.faq_id === faq_id ? { ...it, is_active: nextActive } : it));
    try {
      await axios.put(`${API}/admin/faqs/${faq_id}`, { is_active: nextActive });
      toast.success(nextActive ? "Question is now visible on the page" : "Question hidden");
    } catch (e) {
      // Roll back on error
      setItems(prev => prev.map(it => it.faq_id === faq_id ? { ...it, is_active: !nextActive } : it));
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  };

  const handleDelete = async (faq_id) => {
    if (!window.confirm("Delete this question? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/admin/faqs/${faq_id}`);
      setItems(prev => prev.filter(it => it.faq_id !== faq_id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const persistReorder = async (reordered) => {
    setItems(reordered);
    try {
      const ordered_ids = reordered.map(it => it.faq_id);
      const res = await axios.post(`${API}/admin/faqs/reorder`, { page, ordered_ids });
      // Server returns the canonical list — keep any dirty edits local
      const dirtyMap = new Map(reordered.filter(it => it._dirty).map(it => [it.faq_id, { question: it.question, answer: it.answer }]));
      setItems(res.data.map(srv => {
        const d = dirtyMap.get(srv.faq_id);
        return d ? { ...srv, ...d, _dirty: true } : srv;
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reorder failed");
      fetchItems();
    }
  };

  const handleDragStart = (faq_id) => (e) => {
    if (!isFounder) return;
    setDragId(faq_id);
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs a payload
    try { e.dataTransfer.setData("text/plain", faq_id); } catch { /* noop */ }
  };

  const handleDragOver = (faq_id) => (e) => {
    if (!isFounder || !dragId || dragId === faq_id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverId !== faq_id) setDragOverId(faq_id);
  };

  const handleDragLeave = (faq_id) => () => {
    if (dragOverId === faq_id) setDragOverId(null);
  };

  const handleDrop = (targetId) => (e) => {
    if (!isFounder) return;
    e.preventDefault();
    const sourceId = dragId;
    setDragId(null);
    setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const sourceIdx = items.findIndex(it => it.faq_id === sourceId);
    const targetIdx = items.findIndex(it => it.faq_id === targetId);
    if (sourceIdx < 0 || targetIdx < 0) return;
    const reordered = [...items];
    const [moved] = reordered.splice(sourceIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    persistReorder(reordered);
  };

  const handleDragEnd = () => {
    setDragId(null);
    setDragOverId(null);
  };

  const handleAdd = async () => {
    const q = newDraft.question.trim();
    const a = newDraft.answer.trim();
    if (!q || !a) { toast.error("Question and answer are both required"); return; }
    setAdding(true);
    try {
      const res = await axios.post(`${API}/admin/faqs`, { page, question: q, answer: a, is_active: true });
      setItems(prev => [...prev, res.data]);
      setNewDraft({ question: "", answer: "" });
      toast.success("Question added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    } finally {
      setAdding(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="faq-management-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <HelpCircle className="inline w-7 h-7 mr-2 -mt-1 text-[#8DB5A0]" />
              FAQ Management
            </h1>
            <p className="text-[#64748B] mt-1">
              Drag any question by its handle to reorder, edit inline, hide, or delete. Changes go live immediately.
            </p>
          </div>
          {!isFounder && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-xs font-medium">
              <Lock className="w-3.5 h-3.5" />
              Read-only — founder access required
            </span>
          )}
        </div>

        {/* Page tabs */}
        <div className="inline-flex rounded-full bg-white border border-[#E5DFEC] p-1 mb-6" data-testid="faq-page-tabs">
          {[{ k: "prelaunch", label: "Pre-launch page" }, { k: "launch", label: "Launch page" }].map(t => (
            <button
              key={t.k}
              onClick={() => setPage(t.k)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                page === t.k ? "bg-[#6B4E8A] text-white" : "text-[#64748B] hover:text-[#6B4E8A]"
              }`}
              data-testid={`faq-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-32 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[#E5DFEC] rounded-2xl p-10 text-center mb-6">
            <HelpCircle className="w-10 h-10 mx-auto mb-3 text-[#E5DFEC]" />
            <p className="text-[#6B4E8A] font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>No questions yet</p>
            <p className="text-sm text-[#64748B] mt-1">
              {isFounder ? "Add your first question using the form below." : "The founder hasn't added any FAQs to this page yet."}
            </p>
          </div>
        ) : (
          <div className="space-y-4 mb-8">
            {items.map((it, idx) => (
              <div
                key={it.faq_id}
                draggable={isFounder}
                onDragStart={handleDragStart(it.faq_id)}
                onDragOver={handleDragOver(it.faq_id)}
                onDragLeave={handleDragLeave(it.faq_id)}
                onDrop={handleDrop(it.faq_id)}
                onDragEnd={handleDragEnd}
                className={`bg-white border rounded-2xl p-5 transition-all ${it.is_active ? "border-[#E5DFEC]" : "border-dashed border-[#FCA5A5] bg-[#FEF2F2]/40"} ${dragId === it.faq_id ? "opacity-40 scale-[0.98]" : ""} ${dragOverId === it.faq_id ? "border-[#6B4E8A] border-2 shadow-[0_0_0_3px_rgba(107,78,138,0.12)]" : ""}`}
                data-testid={`faq-row-${it.faq_id}`}
              >
                {/* Top toolbar: drag handle + position + visibility + delete */}
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-1 text-[#64748B] text-xs">
                    <button
                      type="button"
                      className={`p-1 rounded hover:bg-[#F0ECF7] ${isFounder ? "cursor-grab active:cursor-grabbing" : "cursor-not-allowed opacity-40"}`}
                      title={isFounder ? "Drag to reorder" : "Founder only"}
                      aria-label="Drag to reorder"
                      disabled={!isFounder}
                      data-testid={`faq-drag-handle-${it.faq_id}`}
                    >
                      <GripVertical className="w-4 h-4" />
                    </button>
                    <span className="font-medium">#{idx + 1}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-[#64748B] cursor-pointer">
                      <Switch
                        checked={!!it.is_active}
                        onCheckedChange={() => isFounder && handleToggleActive(it.faq_id)}
                        disabled={!isFounder}
                        data-testid={`faq-active-${it.faq_id}`}
                      />
                      {it.is_active
                        ? <span className="inline-flex items-center gap-1 text-[#10B981]"><Eye className="w-3 h-3" /> Visible</span>
                        : <span className="inline-flex items-center gap-1 text-[#94A3B8]"><EyeOff className="w-3 h-3" /> Hidden</span>}
                    </label>
                    <button
                      type="button"
                      onClick={() => handleDelete(it.faq_id)}
                      disabled={!isFounder}
                      className="p-1.5 rounded-lg hover:bg-[#EF4444]/10 disabled:opacity-30 disabled:cursor-not-allowed text-[#EF4444]"
                      title="Delete"
                      data-testid={`faq-delete-${it.faq_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Editable question + answer */}
                <label className="block text-xs font-medium text-[#94A3B8] uppercase tracking-wider mb-1">Question</label>
                <Input
                  value={it.question}
                  onChange={(e) => handleFieldChange(it.faq_id, "question", e.target.value)}
                  disabled={!isFounder}
                  maxLength={300}
                  className="bg-white border-[#E5DFEC] mb-3"
                  data-testid={`faq-question-input-${it.faq_id}`}
                />
                <label className="block text-xs font-medium text-[#94A3B8] uppercase tracking-wider mb-1">Answer</label>
                <Textarea
                  value={it.answer}
                  onChange={(e) => handleFieldChange(it.faq_id, "answer", e.target.value)}
                  disabled={!isFounder}
                  maxLength={3000}
                  rows={4}
                  className="bg-white border-[#E5DFEC] mb-2"
                  data-testid={`faq-answer-input-${it.faq_id}`}
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#94A3B8]">
                    {it._dirty ? <span className="text-[#D97706] font-medium">Unsaved changes</span> : "Saved"}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => handleSaveRow(it.faq_id)}
                    disabled={!isFounder || !it._dirty || savingId === it.faq_id}
                    className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F]"
                    data-testid={`faq-save-${it.faq_id}`}
                  >
                    {savingId === it.faq_id ? "Saving…" : <><Save className="w-3.5 h-3.5 mr-1" /> Save</>}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add new */}
        {isFounder && (
          <div className="bg-[#FAF7F2] border border-dashed border-[#8DB5A0] rounded-2xl p-5" data-testid="faq-add-form">
            <p className="text-sm font-medium text-[#6B4E8A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Plus className="inline w-4 h-4 mr-1 -mt-0.5" />
              Add a new question
            </p>
            <Input
              value={newDraft.question}
              onChange={(e) => setNewDraft(d => ({ ...d, question: e.target.value }))}
              maxLength={300}
              placeholder="What's a question your members keep asking?"
              className="bg-white border-[#E5DFEC] mb-2"
              data-testid="faq-new-question-input"
            />
            <Textarea
              value={newDraft.answer}
              onChange={(e) => setNewDraft(d => ({ ...d, answer: e.target.value }))}
              maxLength={3000}
              rows={3}
              placeholder="The short, honest answer they need."
              className="bg-white border-[#E5DFEC] mb-3"
              data-testid="faq-new-answer-input"
            />
            <Button
              onClick={handleAdd}
              disabled={adding || !newDraft.question.trim() || !newDraft.answer.trim()}
              className="rounded-full bg-[#8DB5A0] text-white hover:bg-[#7AA48F]"
              data-testid="faq-add-btn"
            >
              {adding ? "Adding…" : <><Plus className="w-3.5 h-3.5 mr-1" /> Add to {page === "prelaunch" ? "pre-launch page" : "launch page"}</>}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FAQManagementPage;
